/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type {GitHubPullRequestReviewThread} from './github/pullRequestTimelineTypes';
import type {GitObjectID, Version} from './github/types';

import CommentCount from './CommentCount';
import {
  gitHubPullRequestComparableVersionsAtom,
  gitHubPullRequestIDAtom,
  gitHubPullRequestSelectedVersionIndexAtom,
  gitHubPullRequestThreadsByCommitAtom,
  gitHubPullRequestVersionsAtom,
} from './jotai';
import {countCommentsForThreads, formatISODate, shortOid, versionLabel} from './utils';
import {ActionMenu, Box, Text} from '@primer/react';
import {useAtomValue, useSetAtom} from 'jotai';
import React, {useCallback, useLayoutEffect, useRef} from 'react';

type Props = {
  org: string;
  repo: string;
};

// eslint-disable-next-line prefer-arrow-callback
export default React.memo(function PullRequestVersionSelector({
  org: _org,
  repo: _repo,
}: Props): React.ReactElement {
  const versions = useAtomValue(gitHubPullRequestVersionsAtom);
  const comparableVersions = useAtomValue(gitHubPullRequestComparableVersionsAtom);
  const setComparableVersions = useSetAtom(gitHubPullRequestComparableVersionsAtom);
  const setSelectedVersionIndex = useSetAtom(gitHubPullRequestSelectedVersionIndexAtom);
  const pullRequestID = useAtomValue(gitHubPullRequestIDAtom);
  const reviewThreadsByCommit = useAtomValue(gitHubPullRequestThreadsByCommitAtom);

  const initializedForPRRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (
      pullRequestID != null &&
      initializedForPRRef.current !== pullRequestID &&
      versions.length > 0
    ) {
      initializedForPRRef.current = pullRequestID;
      setSelectedVersionIndex(versions.length - 1);
    }
  }, [pullRequestID, versions, setSelectedVersionIndex]);

  // null = "Base" for from, last version for to
  const fromVersionIndex = findVersionIndex(comparableVersions?.beforeCommitID ?? null, versions);
  const toVersionIndex = findVersionIndex(comparableVersions?.afterCommitID ?? null, versions);

  const onSelectFrom = useCallback(
    (index: number | null) => {
      const afterVersion = toVersionIndex != null ? versions[toVersionIndex] : versions[versions.length - 1];
      if (afterVersion == null) return;
      setComparableVersions({
        beforeCommitID: index != null ? versions[index].headCommit : null,
        afterCommitID: afterVersion.headCommit,
      });
      setSelectedVersionIndex(toVersionIndex ?? versions.length - 1);
    },
    [versions, toVersionIndex, setComparableVersions, setSelectedVersionIndex],
  );

  const onSelectTo = useCallback(
    (index: number) => {
      const beforeCommitID = fromVersionIndex != null ? versions[fromVersionIndex].headCommit : null;
      setComparableVersions({
        beforeCommitID,
        afterCommitID: versions[index].headCommit,
      });
      setSelectedVersionIndex(index);
    },
    [versions, fromVersionIndex, setComparableVersions, setSelectedVersionIndex],
  );

  const fromLabel = fromVersionIndex != null ? versionLabel(fromVersionIndex) : 'Base';
  const toLabel = toVersionIndex != null ? versionLabel(toVersionIndex) : versionLabel(versions.length - 1);

  return (
    <ActionMenu>
      <ActionMenu.Button>
        {fromLabel}
        <Text color="fg.muted" sx={{mx: 1}}>{'\u2192'}</Text>
        {toLabel}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="large">
        <Box sx={{py: 1}}>
          {/* Column headers — wider spacing so "From" and "To" read as separate labels */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              px: 3,
              pb: '6px',
              borderBottom: '1px solid',
              borderColor: 'border.muted',
              mb: 1,
            }}>
            <Text fontSize={0} fontWeight="bold" color="fg.muted">
              From
            </Text>
            <Text fontSize={0} fontWeight="bold" color="fg.muted">
              To
            </Text>
          </Box>

          {/* Base row */}
          <VersionRow
            label="Base"
            fromSelected={fromVersionIndex == null}
            toSelected={false}
            toDisabled={true}
            onSelectFrom={() => onSelectFrom(null)}
            onSelectTo={() => {}}
          />

          {/* Version rows */}
          {versions.map((version, index) => (
            <VersionRow
              key={index}
              label={versionLabel(index)}
              detail={formatISODate(version.headCommittedDate)}
              commitCount={version.commits.length}
              commitHash={shortOid(version.headCommit)}
              commentCount={getCommentCount(version, reviewThreadsByCommit)}
              fromSelected={fromVersionIndex === index}
              toSelected={toVersionIndex === index}
              toDisabled={false}
              onSelectFrom={() => onSelectFrom(index)}
              onSelectTo={() => onSelectTo(index)}
            />
          ))}
        </Box>
      </ActionMenu.Overlay>
    </ActionMenu>
  );
});

function VersionRow({
  label,
  detail,
  commitCount,
  commitHash,
  commentCount,
  fromSelected,
  toSelected,
  toDisabled,
  onSelectFrom,
  onSelectTo,
}: {
  label: string;
  detail?: string;
  commitCount?: number;
  commitHash?: string;
  commentCount?: number;
  fromSelected: boolean;
  toSelected: boolean;
  toDisabled: boolean;
  onSelectFrom: () => void;
  onSelectTo: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '24px 24px 1fr',
        alignItems: 'center',
        columnGap: 1,
        px: 3,
        py: '6px',
        cursor: 'default',
        '&:hover': {bg: 'actionListItem.default.hoverBg'},
        borderRadius: 2,
        mx: 1,
      }}>
      <RadioDot selected={fromSelected} onClick={onSelectFrom} />
      {toDisabled ? (
        <Box sx={{width: 16, height: 16}} />
      ) : (
        <RadioDot selected={toSelected} onClick={onSelectTo} />
      )}
      <Box display="flex" alignItems="center" sx={{gap: 2, fontSize: 1, ml: 1}}>
        <Text fontWeight="bold" sx={{minWidth: 70}}>{label}</Text>
        {detail != null && <Text fontSize={0} color="fg.muted">{detail}</Text>}
        {commitHash != null && (
          <Text fontSize={0} color="fg.muted" sx={{fontFamily: 'mono'}}>{commitHash}</Text>
        )}
        {commitCount != null && (
          <Text fontSize={0} color="fg.muted">
            {commitCount} commit{commitCount === 1 ? '' : 's'}
          </Text>
        )}
        {commentCount != null && commentCount > 0 && <CommentCount count={commentCount} />}
      </Box>
    </Box>
  );
}

function RadioDot({
  selected,
  onClick,
}: {
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      onClick={e => {
        (e as React.MouseEvent).stopPropagation();
        onClick();
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '2px solid',
        borderColor: selected ? 'accent.fg' : 'border.default',
        bg: 'transparent',
        cursor: 'pointer',
        p: 0,
        '&:hover': {
          borderColor: selected ? 'accent.fg' : 'fg.muted',
        },
      }}>
      {selected && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bg: 'accent.fg',
          }}
        />
      )}
    </Box>
  );
}

function getCommentCount(
  version: Version,
  reviewThreadsByCommit: Map<GitObjectID, GitHubPullRequestReviewThread[]>,
): number {
  return version.commits.reduce((acc, c) => {
    const threads = reviewThreadsByCommit.get(c.commit);
    return threads != null ? acc + countCommentsForThreads(threads) : acc;
  }, 0);
}

function findVersionIndex(
  commitID: string | null,
  versions: Version[],
): number | null {
  if (commitID == null) return null;
  const index = versions.findIndex(v => v.headCommit === commitID);
  return index >= 0 ? index : null;
}
